import { isDefined, isNull, isUndefined } from '@aloft/toolkit-shared';

import { useCallback, useEffect, useRef } from 'react';

import { CustomTrash } from './CustomTrash.js';
import { positionsToGeoCoordinates } from './positionsToGeoCoordinates.js';
import { UseMapProps } from './types.js';

type Props<Feature> = {
  mapRef: UseMapProps<Feature>['mapRef'];
  isEnabled: UseMapProps<Feature>['enablePolygonDraw'];
  callbacks: {
    onPolygonDrawCreate: NonNullable<UseMapProps<Feature>['callbacks']>['onPolygonDrawCreate'];
    onPolygonDrawUpdate: NonNullable<UseMapProps<Feature>['callbacks']>['onPolygonDrawUpdate'];
    onPolygonDrawDelete: NonNullable<UseMapProps<Feature>['callbacks']>['onPolygonDrawDelete'];
  };
};

export function usePolygonDraw<Feature>({ mapRef, isEnabled, callbacks }: Props<Feature>) {
  const isMounted = useRef(false);
  const numPolyCoordinates = useRef(0);

  const trashControlRef = useRef<CustomTrash>(
    new CustomTrash(() => {
      mapRef.current?.getMap().fire('draw.delete');
    }),
  );

  const handleDrawCreate = useCallback(
    (event: any) => {
      const features = event.features as mapboxgl.MapboxGeoJSONFeature[];
      if (!features.length) return;

      const feature = features[0];
      if (feature?.geometry.type !== 'Polygon') return;

      const coordinates = feature.geometry.coordinates[0];
      if (!coordinates) return;

      numPolyCoordinates.current = coordinates.length;

      if (isDefined(callbacks.onPolygonDrawCreate)) {
        callbacks.onPolygonDrawCreate(positionsToGeoCoordinates(coordinates));
      }

      mapRef.current?.enableTrashButton();
      mapRef.current?.disablePolygonDrawButton();
    },
    [callbacks, mapRef],
  );

  const handleDrawUpdate = useCallback(
    (event: any) => {
      const features = event.features as mapboxgl.MapboxGeoJSONFeature[];
      if (!features.length) return;

      const feature = features[0];
      if (feature?.geometry.type !== 'Polygon') return;

      const coordinates = feature.geometry.coordinates[0];
      if (!coordinates) return;

      const prevNumCoords = numPolyCoordinates.current;
      const nextNumCoords = coordinates.length;
      numPolyCoordinates.current = nextNumCoords;

      // ignore if new vertex gets added
      if (nextNumCoords > prevNumCoords) return;

      if (isDefined(callbacks.onPolygonDrawUpdate)) {
        callbacks.onPolygonDrawUpdate(positionsToGeoCoordinates(coordinates));
      }

      mapRef.current?.disablePolygonDrawButton();
    },
    [callbacks, mapRef],
  );

  const handleDrawDelete = useCallback(() => {
    numPolyCoordinates.current = 0;

    callbacks.onPolygonDrawDelete?.();

    mapRef.current?.deleteAllPolygons();
    mapRef.current?.disableTrashButton();
    mapRef.current?.enablePolygonDrawButton();
  }, [callbacks, mapRef]);

  useEffect(() => {
    if (!isEnabled || isMounted.current) return;
    if (isNull(mapRef.current)) return;

    const map = mapRef.current.getMap();
    if (isUndefined(map)) return;

    mapRef.current.enablePolygonDrawButton();
    mapRef.current.addControl(trashControlRef.current);
    mapRef.current.disableTrashButton();

    map.on('draw.create', handleDrawCreate);
    map.on('draw.update', handleDrawUpdate);
    map.on('draw.delete', handleDrawDelete);

    isMounted.current = true;
  }, [handleDrawCreate, handleDrawDelete, handleDrawUpdate, isEnabled, mapRef]);
}
